import { getMsToken } from './auth';

const GRAPH_URL = 'https://graph.microsoft.com/v1.0';

const COLORS = ['#C9A84C', '#4a9eff', '#4CAF7C', '#8b7cf6', '#E57373'];

function getInitials(name) {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

async function graphFetch(endpoint, options = {}) {
  const token = await getMsToken();
  if (!token) throw new Error('No Microsoft token');
  const resp = await fetch(`${GRAPH_URL}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!resp.ok) {
    throw new Error(`Graph API error: ${resp.status} ${resp.statusText}`);
  }
  // sendMail and other mutations return 202/204 with an empty body — don't try to parse it
  if (resp.status === 202 || resp.status === 204) return null;
  const text = await resp.text();
  return text ? JSON.parse(text) : null;
}

// ──── User Profile ────
export async function fetchMsUserInfo() {
  try {
    const profile = await graphFetch('/me');
    const name = profile.displayName || profile.userPrincipalName || '';
    const email = profile.mail || profile.userPrincipalName || '';
    const firstName = name.split(' ')[0];
    const initials = getInitials(name);
    // Try to get profile photo
    let picture = null;
    try {
      const token = await getMsToken();
      const photoResp = await fetch(`${GRAPH_URL}/me/photo/$value`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (photoResp.ok) {
        const blob = await photoResp.blob();
        picture = URL.createObjectURL(blob);
      }
    } catch (e) {
      // No photo available
    }
    return { name, email, firstName, initials, picture };
  } catch (e) {
    console.warn('Failed to fetch MS user info:', e);
    return null;
  }
}

// ──── Calendar Events (Today) ────
export async function fetchMsCalendarEvents() {
  try {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    const start = startOfDay.toISOString();
    const end = endOfDay.toISOString();

    const data = await graphFetch(
      `/me/calendarview?startdatetime=${start}&enddatetime=${end}&$orderby=start/dateTime&$top=20&$select=subject,start,end,location,organizer,attendees,isAllDay`
    );

    return (data.value || []).map((event, i) => {
      const startTime = new Date(event.start.dateTime + 'Z');
      const endTime = new Date(event.end.dateTime + 'Z');
      const formatTime = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      const organizer = event.organizer?.emailAddress?.name || '';
      const attendeeCount = (event.attendees || []).length;
      const location = event.location?.displayName || '';
      const sub = [
        organizer ? `Organized by ${organizer}` : '',
        attendeeCount > 0 ? `${attendeeCount} attendee${attendeeCount > 1 ? 's' : ''}` : '',
        location || '',
      ].filter(Boolean).join(' · ');

      return {
        id: event.id || String(i),
        summary: event.subject || '(No title)',
        start: event.isAllDay ? 'All day' : formatTime(startTime),
        end: event.isAllDay ? '' : formatTime(endTime),
        location,
        organizer,
        color: COLORS[i % COLORS.length],
        sub,
        isAllDay: event.isAllDay || false,
      };
    });
  } catch (e) {
    console.warn('Failed to fetch MS calendar:', e);
    return [];
  }
}

// ──── Outlook Emails (Priority Threads) ────
export async function fetchMsEmails() {
  try {
    const data = await graphFetch(
      `/me/messages?$top=15&$orderby=receivedDateTime desc&$select=from,subject,receivedDateTime,importance,isRead,flag&$filter=receivedDateTime ge ${new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()}`
    );

    return (data.value || []).map((msg, i) => {
      const from = msg.from?.emailAddress?.name || msg.from?.emailAddress?.address || 'Unknown';
      const email = msg.from?.emailAddress?.address || '';
      let priority = 'dim';
      if (msg.importance === 'high') priority = 'red';
      else if (msg.importance === 'normal' && !msg.isRead) priority = 'gold';
      else if (!msg.isRead) priority = 'blue';
      if (msg.flag?.flagStatus === 'flagged') priority = 'gold';

      return {
        from,
        email,
        subject: msg.subject || '(No subject)',
        time: timeAgo(msg.receivedDateTime),
        priority,
        initials: getInitials(from),
        color: COLORS[i % COLORS.length],
      };
    });
  } catch (e) {
    console.warn('Failed to fetch MS emails:', e);
    return [];
  }
}

// ──── OneDrive Files (Recent) ────
export async function fetchMsFiles() {
  try {
    const data = await graphFetch(
      `/me/drive/recent?$top=10&$select=name,lastModifiedBy,lastModifiedDateTime,webUrl,file`
    );

    return (data.value || []).map((file, i) => {
      const modifiedBy = file.lastModifiedBy?.user?.displayName || 'Unknown';
      const ext = file.name?.split('.').pop()?.toLowerCase() || '';
      let icon = 'doc';
      let project = 'OneDrive';
      if (['xlsx', 'xls', 'csv'].includes(ext)) icon = 'sheets';
      else if (['pptx', 'ppt'].includes(ext)) icon = 'brief';
      else if (['pdf'].includes(ext)) icon = 'doc';

      return {
        title: file.name || 'Untitled',
        modifiedBy,
        modified: timeAgo(file.lastModifiedDateTime),
        url: file.webUrl || '#',
        icon,
        color: COLORS[i % COLORS.length],
        project,
      };
    });
  } catch (e) {
    console.warn('Failed to fetch MS files:', e);
    return [];
  }
}

// ──── Create Outlook Draft ────
export async function createMsDraft(to, subject, body) {
  try {
    const draft = await graphFetch('/me/messages', {
      method: 'POST',
      body: JSON.stringify({
        subject,
        body: {
          contentType: 'Text',
          content: body,
        },
        toRecipients: to ? [{
          emailAddress: { address: to },
        }] : [],
        isDraft: true,
      }),
    });
    return draft;
  } catch (e) {
    console.warn('Failed to create MS draft:', e);
    return null;
  }
}

// ──── Send Email ────
export async function sendMsEmail(to, subject, body) {
  try {
    await graphFetch('/me/sendMail', {
      method: 'POST',
      body: JSON.stringify({
        message: {
          subject,
          body: { contentType: 'Text', content: body },
          toRecipients: [{ emailAddress: { address: to } }],
        },
      }),
    });
    return { success: true };
  } catch (e) {
    console.warn('Failed to send MS email:', e);
    return null;
  }
}

// ──── Search Emails ────
export async function searchMsEmails(query, maxResults = 5) {
  try {
    const result = await graphFetch(`/me/messages?$search="${encodeURIComponent(query)}"&$top=${maxResults}&$select=subject,from,receivedDateTime,bodyPreview`);
    return (result.value || []).map(m => ({
      id: m.id,
      from: m.from?.emailAddress?.name || m.from?.emailAddress?.address || 'Unknown',
      subject: m.subject,
      date: m.receivedDateTime,
      snippet: m.bodyPreview?.slice(0, 120) || '',
    }));
  } catch (e) {
    console.warn('Failed to search MS emails:', e);
    return [];
  }
}

// ──── Search OneDrive ────
export async function searchMsDrive(query, maxResults = 5) {
  try {
    const result = await graphFetch(`/me/drive/root/search(q='${encodeURIComponent(query)}')?$top=${maxResults}`);
    return (result.value || []).map(f => ({
      id: f.id,
      title: f.name,
      modified: f.lastModifiedDateTime,
      url: f.webUrl,
      modifiedBy: f.lastModifiedBy?.user?.displayName || 'Unknown',
    }));
  } catch (e) {
    console.warn('Failed to search MS Drive:', e);
    return [];
  }
}

// ──── Create Calendar Event ────
export async function createMsCalendarEvent(subject, startTime, endTime, attendees = []) {
  try {
    const event = await graphFetch('/me/events', {
      method: 'POST',
      body: JSON.stringify({
        subject,
        start: { dateTime: startTime, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
        end: { dateTime: endTime, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
        attendees: attendees.map(email => ({
          emailAddress: { address: email },
          type: 'required',
        })),
      }),
    });
    return event;
  } catch (e) {
    console.warn('Failed to create MS calendar event:', e);
    return null;
  }
}

// ──── Fetch All Microsoft Data ────
export async function fetchAllMsData() {
  const [calendar, emails, files] = await Promise.allSettled([
    fetchMsCalendarEvents(),
    fetchMsEmails(),
    fetchMsFiles(),
  ]);

  return {
    calendar: calendar.status === 'fulfilled' ? calendar.value : [],
    gmail: {
      priorityThreads: emails.status === 'fulfilled' ? emails.value : [],
      unreadCount: 0,
    },
    drive: files.status === 'fulfilled' ? files.value : [],
    stats: {
      emailsToday: 0,
      emailsFlagged: (emails.status === 'fulfilled' ? emails.value : []).filter(e => e.priority === 'gold' || e.priority === 'red').length,
      meetings: calendar.status === 'fulfilled' ? calendar.value.length : 0,
      meetingsTotal: calendar.status === 'fulfilled' ? calendar.value.length : 0,
      investorPipeline: 'N/A',
      investorStatus: 'Connect Outlook for updates',
    },
  };
}
